import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";
import {
  AKA_TRAVEL_PRICE_STEP,
  AKA_TRAVEL_YOUR_PRODUCT_STEP,
} from "../akaTravelSteps";
import {
  getCoverageToDisplay,
  getInitialFormStateWithCorrectedAkaTravelCoverage,
} from "./helpers";

import { AkaRenewalInfoAlert } from "Components/AkaRenewalInfoAlert";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products/index";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { formBooleanToBoolean } from "Utils/formUtils";
import React from "react";
import { useSelector } from "react-redux";
import { AkaTravelProductIntroFormValues } from "../ProductIntro/akaTravelProductIntroInterfaces";
import { useGetPriceCalculationResultLoadingState } from "../hooks";
import { AkaTravelCoverages } from "./AkaTravelCoverages";
import { AkaTravelProductPriceFormValues } from "./akaTravelProductPriceInterfaces";

const stepId = AKA_TRAVEL_PRICE_STEP.id;

export const AkaTravelProductPrice = (): React.ReactElement => {
  const [
    {
      formText: { checkPrice },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.AKA_TRAVEL));
  const isPriceLoading = useGetPriceCalculationResultLoadingState();
  const formProductIntro: AkaTravelProductIntroFormValues = useFormState(
    AKA_TRAVEL_YOUR_PRODUCT_STEP.id,
  );
  const handleAddProductToCart = useAddProductToCartHandler(stepId);
  const coverageToDisplay = getCoverageToDisplay(formProductIntro);

  const initialFormState = getInitialFormStateWithCorrectedAkaTravelCoverage(
    useFormState(stepId, {
      coverage: coverageToDisplay,
    }) as AkaTravelProductPriceFormValues,
    formBooleanToBoolean(formProductIntro.isInsuranceFor),
  );

  const { addProductToCart } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formProps: AkaTravelProductPriceFormValues): any => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.AKA_TRAVEL,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.AKA_TRAVEL}`,
        );
      }}
    >
      <ProductPriceTile
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={checkPrice.header[lang]}
      >
        {(formProps: FormikProps<AkaTravelProductPriceFormValues>) => {
          return (
            <Form>
              <AkaRenewalInfoAlert
                productName={INSURANCE_NAME_VALUE.AKA_TRAVEL}
              />
              <AkaTravelCoverages
                formProps={formProps}
                isPriceLoading={isPriceLoading}
              />
              {addProductToCart.error && (
                <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
              )}
              <StyledComparisonWrap>
                <NavigationButtons isLoading={addProductToCart.isLoading} />
              </StyledComparisonWrap>
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_TRAVEL}
                stepId={stepId}
              />
            </Form>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
